import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import WhatsAppButton from '../../components/ui/WhatsAppButton';
import { useNavigate } from 'react-router-dom';



const InformationRequestForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [urgencyLevel, setUrgencyLevel] = useState('');
  const [preferredContact, setPreferredContact] = useState([]);

  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    mode: 'onChange'
  });

  const totalSteps = 4;

  const serviceCategories = [
    {
      id: 'formation',
      name: 'Formation Professionnelle',
      icon: 'GraduationCap',
      description: 'Management RH, Projets Numériques, IA, Gestion de Projets'
    },
    {
      id: 'conseil',
      name: 'Conseil Stratégique',
      icon: 'Target',
      description: 'Transformation organisationnelle et stratégie d\'entreprise'
    },
    {
      id: 'ia',
      name: 'Intelligence Artificielle',
      icon: 'Brain',
      description: 'Solutions IA éthiques et performantes pour votre organisation'
    },
    {
      id: 'recherche',
      name: 'Études & Recherches',
      icon: 'Search',
      description: 'Données fiables et analyses stratégiques approfondies'
    },
    {
      id: 'marketing',
      name: 'Marketing Digital',
      icon: 'Smartphone',
      description: 'Stratégies digitales et transformation marketing'
    },
    {
      id: 'rh',
      name: 'Gestion RH',
      icon: 'Users',
      description: 'Optimisation des processus RH et gestion des talents'
    },
    {
      id: 'projets',
      name: 'Gestion de Projets Digitaux',
      icon: 'CheckSquare',
      description: 'Excellence opérationnelle et pilotage de projets'
    },
    {
      id: 'therapie',
      name: 'Thérapie d\'Entreprise',
      icon: 'Heart',
      description: 'Accompagnement et transformation organisationnelle'
    }
  ];

  const urgencyOptions = [
    { value: 'immediate', label: 'Immédiat (< 1 semaine)', icon: 'AlertCircle', color: '#96121D' },
    { value: 'urgent', label: 'Urgent (1-4 semaines)', icon: 'Clock', color: '#9B8E57' },
    { value: 'planned', label: 'Planifié (1-3 mois)', icon: 'Calendar', color: '#96121D' },
    { value: 'future', label: 'Futur (3+ mois)', icon: 'TrendingUp', color: '#9B8E57' }
  ];

  const contactMethods = [
    { value: 'email', label: 'Email', icon: 'Mail' },
    { value: 'phone', label: 'Téléphone', icon: 'Phone' },
    { value: 'video', label: 'Visioconférence', icon: 'Video' },
    { value: 'meeting', label: 'Réunion sur site', icon: 'Users' }
  ];

  const handleServiceToggle = (serviceId) => {
    setSelectedServices(prev => 
      prev?.includes(serviceId) 
        ? prev?.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleContactToggle = (method) => {
    setPreferredContact(prev => 
      prev?.includes(method) 
        ? prev?.filter(m => m !== method)
        : [...prev, method]
    );
  };

  const onSubmit = async (data) => {
    try {
      // Define tracking functions if they don't exist
      const trackFormSubmission = (formName, formData) => {
        console.log('Form Submission:', formName, formData);
        // Add actual tracking implementation here
      };

      const trackLeadGeneration = (leadType, leadData) => {
        console.log('Lead Generation:', leadType, leadData);
        // Add actual tracking implementation here
      };

      // Track form submission
      trackFormSubmission('information_request_form', {
        user_name: `${data?.firstName} ${data?.lastName}`,
        user_email: data?.email,
        user_phone: data?.phone,
        user_company: data?.company,
        information_type: data?.informationType,
        urgency_level: urgencyLevel
      });

      // Track lead generation
      trackLeadGeneration('information_request', {
        email: data?.email,
        company: data?.company,
        name: `${data?.firstName} ${data?.lastName}`,
        information_type: data?.informationType
      });

      console.log('Form Data:', {
        ...data,
        selectedServices,
        urgencyLevel,
        preferredContact
      });
      
      await new Promise(resolve => setTimeout(resolve, 1500));
      setIsSubmitted(true);
    } catch (error) {
      console.error('Submission error:', error);
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch(currentStep) {
      case 1:
        return watch('firstName') && watch('lastName') && watch('email') && watch('phone');
      case 2:
        return selectedServices?.length > 0;
      case 3:
        return watch('projectDescription') && urgencyLevel;
      case 4:
        return preferredContact?.length > 0;
      default:
        return false;
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <WhatsAppButton />
        
        <div className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
              <div className="p-8 lg:p-12 text-center">
                {/* Success Icon */}
                <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg" style={{background: 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)'}}>
                  <Icon name="CheckCircle" size={48} className="text-white" />
                </div>
                
                {/* Title */}
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  Demande Envoyée avec Succès !
                </h1>
                
                {/* Description */}
                <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto">
                  Merci pour votre confiance. Notre équipe d'experts <span className="font-bold" style={{color: '#96121D'}}>CRF PERFECTION</span> analyse votre demande et vous contactera dans les <strong>24 heures ouvrées</strong>.
                </p>
                
                {/* Info Cards */}
                <div className="grid md:grid-cols-3 gap-6 mb-10">
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4" style={{backgroundColor: 'rgba(150, 18, 29, 0.1)'}}>
                      <Icon name="Clock" size={28} style={{color: '#96121D'}} />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2 text-lg">Réponse Rapide</h3>
                    <p className="text-sm text-gray-600">Sous 24h ouvrées</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4" style={{backgroundColor: 'rgba(155, 142, 87, 0.1)'}}>
                      <Icon name="Users" size={28} style={{color: '#9B8E57'}} />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2 text-lg">Expert Dédié</h3>
                    <p className="text-sm text-gray-600">Consultation personnalisée</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4" style={{backgroundColor: 'rgba(150, 18, 29, 0.1)'}}>
                      <Icon name="Award" size={28} style={{color: '#96121D'}} />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2 text-lg">Excellence Garantie</h3>
                    <p className="text-sm text-gray-600">Standards internationaux</p>
                  </div>
                </div>
                
                {/* Contact Info */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200 mb-8">
                  <h3 className="font-bold text-gray-900 mb-4 text-lg">Besoin d'une réponse immédiate ?</h3>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <a href="tel:+12409784155" className="flex items-center gap-2 font-semibold hover:underline" style={{color: '#96121D'}}>
                      <Icon name="Phone" size={20} />
                      +1 (240) 978-4155
                    </a>
                    <span className="hidden sm:block text-gray-400">|</span>
                    <a href="mailto:contact@crfperfection.pro" className="flex items-center gap-2 font-semibold hover:underline" style={{color: '#9B8E57'}}>
                      <Icon name="Mail" size={20} />
                      contact@crfperfection.pro
                    </a>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="lg"
                    onClick={() => navigate('/')}
                    className="text-white shadow-lg hover:shadow-xl transition-all duration-300"
                    style={{background: 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)'}}
                  >
                    <Icon name="Home" size={20} className="mr-2" />
                    Retour à l'Accueil
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate('/contact')}
                    className="border-2 hover:bg-gray-50"
                    style={{borderColor: '#96121D', color: '#96121D'}}
                  >
                    <Icon name="Phone" size={20} className="mr-2" />
                    Nous Contacter
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <WhatsAppButton />
      {/* Hero Section - Dark with CRF Branding */}
      <section className="relative overflow-hidden pt-32 pb-16 bg-gradient-to-br from-gray-900 via-[#1a0a0d] to-black">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-64 h-64 rounded-full" style={{background: 'radial-gradient(circle, #96121D 0%, transparent 70%)'}}></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full" style={{background: 'radial-gradient(circle, #9B8E57 0%, transparent 70%)'}}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-6 border" style={{backgroundColor: 'rgba(150, 18, 29, 0.1)', borderColor: 'rgba(150, 18, 29, 0.3)'}}>
              <Icon name="FileText" size={18} style={{color: '#9B8E57'}} />
              <span className="text-sm font-semibold" style={{color: '#9B8E57'}}>Demande d'Information</span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Obtenez une{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: 'linear-gradient(to right, #96121D, #9B8E57)'}}>
                Consultation Personnalisée
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Remplissez ce formulaire en 4 étapes simples. Notre équipe d'experts CRF PERFECTION vous contactera dans les 24 heures pour discuter de vos besoins spécifiques.
            </p>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="text-3xl font-bold mb-1" style={{color: '#9B8E57'}}>24h</div>
                <div className="text-sm text-gray-400">Réponse garantie</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="text-3xl font-bold mb-1" style={{color: '#9B8E57'}}>100%</div>
                <div className="text-sm text-gray-400">Gratuit</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="text-3xl font-bold mb-1" style={{color: '#9B8E57'}}>8+</div>
                <div className="text-sm text-gray-400">Services experts</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="text-3xl font-bold mb-1" style={{color: '#9B8E57'}}>95%</div>
                <div className="text-sm text-gray-400">Satisfaction client</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Form Section */}
      <section className="py-16 -mt-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
            {/* Progress Bar */}
            <div className="bg-gradient-to-r from-gray-50 to-white px-8 py-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                {[1, 2, 3, 4]?.map((step) => (
                  <div key={step} className="flex items-center flex-1">
                    <div className="flex flex-col items-center relative">
                      <div 
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                          currentStep >= step ? 'text-white shadow-lg' : 'bg-gray-200 text-gray-500'
                        }`}
                        style={currentStep >= step ? {background: 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)'} : {}}
                      >
                        {currentStep > step ? <Icon name="Check" size={20} /> : step}
                      </div>
                      <span className="text-xs mt-2 font-medium text-gray-600 absolute -bottom-6 whitespace-nowrap">
                        {step === 1 && 'Informations'}
                        {step === 2 && 'Services'}
                        {step === 3 && 'Projet'}
                        {step === 4 && 'Contact'}
                      </span>
                    </div>
                    {step < 4 && (
                      <div className="flex-1 h-1 mx-2 rounded-full bg-gray-200 relative overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: currentStep > step ? '100%' : '0%',
                            background: 'linear-gradient(90deg, #96121D 0%, #9B8E57 100%)'
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmit(onSubmit)} className="p-8 lg:p-12">
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Vos Informations</h2>
                    <p className="text-gray-600">Commençons par quelques informations de base</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Prénom <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...register('firstName', { required: true })}
                        placeholder="Votre prénom"
                        className="w-full"
                      />
                      {errors?.firstName && <span className="text-sm text-red-500 mt-1">Ce champ est requis</span>}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nom <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...register('lastName', { required: true })}
                        placeholder="Votre nom"
                        className="w-full"
                      />
                      {errors?.lastName && <span className="text-sm text-red-500 mt-1">Ce champ est requis</span>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Professionnel <span className="text-red-500">*</span>
                    </label>
                    <Input
                      {...register('email', { 
                        required: true,
                        pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                      })}
                      type="email"
                      placeholder="votre.email@entreprise.com"
                      className="w-full"
                    />
                    {errors?.email && <span className="text-sm text-red-500 mt-1">Email valide requis</span>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Téléphone <span className="text-red-500">*</span>
                      </label>
                      <Input
                        {...register('phone', { required: true })}
                        type="tel"
                        placeholder="+1 (240) 978-4155"
                        className="w-full"
                      />
                      {errors?.phone && <span className="text-sm text-red-500 mt-1">Ce champ est requis</span>}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Entreprise
                      </label>
                      <Input
                        {...register('company')}
                        placeholder="Nom de votre entreprise"
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Fonction
                    </label>
                    <Input
                      {...register('position')}
                      placeholder="Votre fonction dans l'entreprise"
                      className="w-full"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Service Selection */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Services Souhaités</h2>
                    <p className="text-gray-600">Sélectionnez un ou plusieurs services qui vous intéressent</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {serviceCategories?.map((service) => (
                      <div
                        key={service?.id}
                        onClick={() => handleServiceToggle(service?.id)}
                        className={`relative rounded-2xl p-6 border-2 cursor-pointer transition-all duration-300 ${
                          selectedServices?.includes(service?.id)
                            ? 'border-[#96121D] bg-gradient-to-br from-[#96121D]/5 to-[#9B8E57]/5 shadow-lg'
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                        }`}
                      >
                        {selectedServices?.includes(service?.id) && (
                          <div className="absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center" style={{backgroundColor: '#96121D'}}>
                            <Icon name="Check" size={16} className="text-white" />
                          </div>
                        )}
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{backgroundColor: selectedServices?.includes(service?.id) ? 'rgba(150, 18, 29, 0.1)' : 'rgba(155, 142, 87, 0.1)'}}>
                            <Icon name={service?.icon} size={24} style={{color: selectedServices?.includes(service?.id) ? '#96121D' : '#9B8E57'}} />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900 mb-1">{service?.name}</h3>
                            <p className="text-sm text-gray-600">{service?.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {selectedServices?.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500">Veuillez sélectionner au moins un service</p>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Project Details */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Détails du Projet</h2>
                    <p className="text-gray-600">Parlez-nous de votre projet et de vos besoins</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description du Projet <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      {...register('projectDescription', { required: true })}
                      rows={6}
                      placeholder="Décrivez votre projet, vos objectifs, vos défis actuels..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#96121D] focus:border-transparent resize-none"
                    />
                    {errors?.projectDescription && <span className="text-sm text-red-500 mt-1">Ce champ est requis</span>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Niveau d'Urgence <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {urgencyOptions?.map((option) => (
                        <div
                          key={option?.value}
                          onClick={() => setUrgencyLevel(option?.value)}
                          className={`rounded-xl p-4 border-2 cursor-pointer transition-all duration-300 ${
                            urgencyLevel === option?.value
                              ? 'border-[#96121D] bg-gradient-to-br from-[#96121D]/5 to-[#9B8E57]/5 shadow-lg'
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: urgencyLevel === option?.value ? 'rgba(150, 18, 29, 0.1)' : 'rgba(155, 142, 87, 0.1)'}}>
                              <Icon name={option?.icon} size={20} style={{color: option?.color}} />
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{option?.label}</p>
                            </div>
                            {urgencyLevel === option?.value && (
                              <Icon name="CheckCircle" size={20} style={{color: '#96121D'}} />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Budget Estimé (Optionnel)
                    </label>
                    <Select {...register('budget')} className="w-full">
                      <option value="">Sélectionnez une fourchette</option>
                      <option value="5k-10k">5 000 $ - 10 000 $</option>
                      <option value="10k-25k">10 000 $ - 25 000 $</option>
                      <option value="25k-50k">25 000 $ - 50 000 $</option>
                      <option value="50k-100k">50 000 $ - 100 000 $</option>
                      <option value="100k+">100 000 $ +</option>
                    </Select>
                  </div>
                </div>
              )}

              {/* Step 4: Contact Preferences */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Préférences de Contact</h2>
                    <p className="text-gray-600">Comment souhaitez-vous être contacté ?</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Méthodes de Contact Préférées <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {contactMethods?.map((method) => (
                        <div
                          key={method?.value}
                          onClick={() => handleContactToggle(method?.value)}
                          className={`rounded-xl p-4 border-2 cursor-pointer transition-all duration-300 ${
                            preferredContact?.includes(method?.value)
                              ? 'border-[#96121D] bg-gradient-to-br from-[#96121D]/5 to-[#9B8E57]/5 shadow-lg'
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: preferredContact?.includes(method?.value) ? 'rgba(150, 18, 29, 0.1)' : 'rgba(155, 142, 87, 0.1)'}}>
                              <Icon name={method?.icon} size={20} style={{color: preferredContact?.includes(method?.value) ? '#96121D' : '#9B8E57'}} />
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{method?.label}</p>
                            </div>
                            {preferredContact?.includes(method?.value) && (
                              <Icon name="CheckCircle" size={20} style={{color: '#96121D'}} />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Disponibilité Préférée
                    </label>
                    <Select {...register('availability')} className="w-full">
                      <option value="">Sélectionnez un créneau</option>
                      <option value="morning">Matin (9h - 12h)</option>
                      <option value="afternoon">Après-midi (14h - 17h)</option>
                      <option value="evening">Soir (17h - 19h)</option>
                      <option value="flexible">Flexible</option>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Informations Complémentaires
                    </label>
                    <textarea
                      {...register('additionalInfo')}
                      rows={4}
                      placeholder="Ajoutez toute information supplémentaire qui pourrait nous aider à mieux vous servir..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#96121D] focus:border-transparent resize-none"
                    />
                  </div>

                  {/* Privacy Notice */}
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-start gap-3">
                      <Icon name="Shield" size={20} style={{color: '#9B8E57'}} className="flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Confidentialité & Sécurité</h4>
                        <p className="text-sm text-gray-600">
                          Vos informations sont traitées de manière confidentielle et sécurisée. Nous ne partageons jamais vos données avec des tiers. En soumettant ce formulaire, vous acceptez d'être contacté par CRF PERFECTION concernant votre demande.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-10 pt-8 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="border-2"
                  style={{borderColor: currentStep === 1 ? '#e5e7eb' : '#96121D', color: currentStep === 1 ? '#9ca3af' : '#96121D'}}
                >
                  <Icon name="ChevronLeft" size={20} className="mr-2" />
                  Précédent
                </Button>

                {currentStep < totalSteps ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    disabled={!canProceed()}
                    className="text-white shadow-lg hover:shadow-xl transition-all duration-300"
                    style={{background: canProceed() ? 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)' : '#e5e7eb'}}
                  >
                    Suivant
                    <Icon name="ChevronRight" size={20} className="ml-2" />
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={!canProceed()}
                    className="text-white shadow-lg hover:shadow-xl transition-all duration-300"
                    style={{background: canProceed() ? 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)' : '#e5e7eb'}}
                  >
                    <Icon name="Send" size={20} className="mr-2" />
                    Envoyer la Demande
                  </Button>
                )}
              </div>
            </form>
          </div>

          {/* Contact Info Card */}
          <div className="mt-8 bg-gradient-to-br from-gray-900 via-[#1a0a0d] to-black rounded-2xl p-8 text-white">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold mb-2">Besoin d'Aide Immédiate ?</h3>
              <p className="text-gray-300">Notre équipe est disponible pour répondre à vos questions</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{backgroundColor: 'rgba(155, 142, 87, 0.2)'}}>
                  <Icon name="Phone" size={24} style={{color: '#9B8E57'}} />
                </div>
                <p className="text-sm text-gray-400 mb-1">Téléphone</p>
                <a href="tel:+12409784155" className="font-semibold hover:underline" style={{color: '#9B8E57'}}>+1 (240) 978-4155</a>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{backgroundColor: 'rgba(155, 142, 87, 0.2)'}}>
                  <Icon name="Mail" size={24} style={{color: '#9B8E57'}} />
                </div>
                <p className="text-sm text-gray-400 mb-1">Email</p>
                <a href="mailto:contact@crfperfection.pro" className="font-semibold hover:underline" style={{color: '#9B8E57'}}>contact@crfperfection.pro</a>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{backgroundColor: 'rgba(155, 142, 87, 0.2)'}}>
                  <Icon name="MapPin" size={24} style={{color: '#9B8E57'}} />
                </div>
                <p className="text-sm text-gray-400 mb-1">Adresse</p>
                <p className="text-sm font-semibold text-white">Washington D.C. 20006</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default InformationRequestForm;